<template>
  <div class="chatbot-page flex flex-col flex-1 min-h-0 overflow-hidden">
    <!-- Start picker: general vs a sport-specific chat (also used to switch).
         It takes over the whole page rather than sitting in a modal — choosing
         an environment is the first step of the flow, not an interruption. -->
    <context-picker
      v-if="!contextChosen"
      :contexts="contexts"
      :current="context"
      @select="selectContext"
      @ask="askFromPicker"
      @close="contextChosen = true"
    />

    <!-- Page header: title left, context + role select + reset right -->
    <header
      v-if="contextChosen"
      class="chatbot-header flex-shrink-0 flex justify-between items-center px-6 py-4"
    >
      <h3 class="page-titles chatbot-title">
        {{ envTitle }}
      </h3>
      <div class="flex items-center gap-2">
        <!-- Saved chat history (only when saving is switched on in the chatbot) -->
        <button
          v-if="historyEnabled"
          class="history-toggle"
          :title="historyOpen ? 'Hide your saved chats' : 'Show your saved chats'"
          @click="toggleHistory"
        >
          {{ historyOpen ? 'Hide chats' : 'Your chats' }}
        </button>
        <!-- The page title already names the active environment, so this is
             just the action to switch — no need to repeat the name here, and
             no caret since it opens a full page rather than a dropdown. -->
        <button
          class="context-chip"
          title="Switch to a different analysis environment"
          @click="openPicker"
        >
          Change context
        </button>
        <select
          v-model="role"
          title="Tailor answers for this role"
          class="role-select"
        >
          <option value="">
            No role (general)
          </option>
          <option
            v-for="r in roles"
            :key="r.id"
            :value="r.id"
          >
            {{ r.name }}
          </option>
        </select>

        <!-- Saved conversations (this user's own). Feature disabled for now
             (see `savedFeatureEnabled`) — the markup and its methods stay in
             place so it is a one-line flip to bring back. -->
        <div
          v-if="savedFeatureEnabled"
          style="position: relative;"
        >
          <button
            class="new-conversation-btn"
            title="Your saved conversations"
            @click="toggleSavedMenu"
          >
            Saved ▾
          </button>
          <div
            v-if="savedMenuOpen"
            style="position:absolute; right:0; margin-top:0.4rem; width:20rem; max-height:24rem; overflow-y:auto; background:#fff; border:1px solid #e5e7eb; border-radius:0.75rem; box-shadow:0 10px 25px rgba(0,0,0,0.15); z-index:50; padding:0.4rem;"
          >
            <div
              v-if="!savedList.length"
              style="padding:0.5rem 0.75rem; color:#6b7280; font-size:0.85rem;"
            >
              No saved conversations yet.
            </div>
            <button
              v-for="c in savedList"
              :key="c.id"
              style="display:block; width:100%; text-align:left; padding:0.5rem 0.75rem; border:none; background:none; cursor:pointer; border-radius:0.5rem;"
              @click="openSaved(c.id)"
            >
              <div style="font-size:0.9rem; font-weight:600; color:#111827;">
                {{ c.title || '(untitled)' }}
              </div>
              <div style="font-size:0.75rem; color:#6b7280;">
                {{ c.visibility === 'shared' ? '🌐 shared' : '🔒 private' }} · {{ (c.created_at || '').slice(0, 10) }}<span v-if="c.context"> · {{ c.context }}</span>
              </div>
            </button>
          </div>
        </div>

        <!-- Save the current conversation as knowledge -->
        <select
          v-if="savedFeatureEnabled && started"
          v-model="saveVisibility"
          title="Who should this saved conversation be able to help?"
          class="role-select"
        >
          <option value="private">
            Private (only me)
          </option>
          <option value="shared">
            Shared (everyone)
          </option>
        </select>
        <button
          v-if="savedFeatureEnabled && started"
          class="new-conversation-btn"
          :disabled="saving"
          title="Distill this conversation and keep it as knowledge for future sessions"
          @click="saveConversationToKnowledge"
        >
          {{ saving ? 'Saving…' : (saveFlash ? 'Saved ✓' : 'Remember this conversation') }}
        </button>

        <!-- A/B switch for the internal ChatGPT comparison (admins and the
             Chatbot Test organisation only, see ../backend.js) -->
        <select
          v-if="canSwitch"
          v-model="backend"
          title="Internal test: which chatbot answers. Note A or B in your feedback."
          class="role-select"
          @change="switchBackend"
        >
          <option value="A">
            Chatbot A
          </option>
          <option value="B">
            Chatbot B
          </option>
        </select>

        <button
          class="new-conversation-btn"
          @click="resetConversation"
        >
          New conversation
        </button>

        <!-- Admin dashboard: edit agent behaviour + manage knowledge files.
             Hidden for now (Oct 2026): open it by typing /chatbot/settings. -->
        <router-link
          v-if="false"
          class="admin-gear"
          title="Chatbot behaviour settings"
          :to="{ name: 'ChatbotSettings' }"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle
              cx="12"
              cy="12"
              r="3"
            />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
        </router-link>
      </div>
    </header>

    <!-- Re-ingest running notice (knowledge index is being rebuilt) -->
    <div
      v-if="reingestRunning"
      class="reingest-notice"
    >
      ⏳ Knowledge index is being rebuilt — answers may not reflect the latest documents yet.
      <button
        class="reingest-notice-close"
        @click="reingestRunning = false"
      >
        ✕
      </button>
    </div>

    <!-- Content row: chat column + sources panel -->
    <div
      v-if="contextChosen"
      class="relative flex flex-1 min-h-0 overflow-hidden"
    >
      <!-- Saved chats (left) -->
      <history-panel
        v-if="historyEnabled && historyOpen"
        :chats="chats"
        :active-id="activeConversationId"
        :loading="chatsLoading"
        :error="chatsError"
        :consent="consent"
        :contexts="contexts"
        @new="startNewChat"
        @open="openChat"
        @rename="renameSavedChat"
        @delete="deleteSavedChat"
        @consent="answerConsent"
        @close="toggleHistory"
      />

      <!-- Chat column -->
      <div
        ref="chatCol"
        class="chat-col flex flex-col flex-1 min-w-0 overflow-y-auto"
        :class="{ 'chat-col--centered': !panelOpen, 'chat-col--hero': !started }"
      >
        <consent-prompt
          v-if="showConsentPrompt && !started"
          class="mt-4"
          :busy="consentBusy"
          @answer="answerConsent"
        />
        <chat-hero
          v-if="!started"
          :context="context"
          :context-name="contextName"
          @send="sendFromHero"
        />

        <template v-else>
          <section class="chat-conversation pt-4">
            <focus-badge :focus="focus" />
            <message-list
              :messages="messages"
              :typing="inflight"
              :task="currentTask"
              :feedback-enabled="historyEnabled"
              @focus-sources="focusSourcesPanel"
              @feedback="sendFeedback"
            />
          </section>

          <consent-prompt
            v-if="showConsentPrompt"
            :busy="consentBusy"
            @answer="answerConsent"
          />
          <message-input @send="send" />
        </template>
      </div>

      <!-- Sources panel (right) -->
      <sources-panel
        v-if="panelOpen"
        :sources="sources"
        :org="org"
        :width="sourcesPanelWidth"
        @close="closeSources"
        @resize="sourcesPanelWidth = $event"
      />
    </div>

    <!-- Edge tab to reopen the sources panel after it's been hidden -->
    <button
      v-if="contextChosen && !panelOpen && hasSources"
      class="src-tab src-tab--open"
      title="Show context"
      @click="reopenSources"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      ><polyline points="15 18 9 12 15 6" /></svg>
      <span>Context</span>
    </button>

    <!-- Saved-conversation reader -->
    <div
      v-if="reader"
      style="position:fixed; inset:0; background:rgba(0,0,0,0.4); z-index:60; display:flex; align-items:center; justify-content:center; padding:1rem;"
      @click.self="closeReader"
    >
      <div style="background:#fff; border-radius:0.75rem; box-shadow:0 20px 50px rgba(0,0,0,0.25); max-width:42rem; width:100%; max-height:85vh; overflow-y:auto; padding:1.5rem;">
        <div
          class="flex justify-between items-start gap-4"
          style="margin-bottom:0.5rem;"
        >
          <h3 style="font-size:1.1rem; font-weight:600; color:#111827;">
            {{ reader.title || '(untitled)' }}
          </h3>
          <button
            style="font-size:1.25rem; line-height:1; background:none; border:none; cursor:pointer; color:#6b7280;"
            @click="closeReader"
          >
            ✕
          </button>
        </div>
        <div style="font-size:0.75rem; color:#6b7280; margin-bottom:1rem;">
          {{ reader.visibility === 'shared' ? '🌐 shared' : '🔒 private' }} · {{ (reader.created_at || '').slice(0, 19).replace('T', ' ') }}<span v-if="reader.context"> · {{ reader.context }}</span>
        </div>
        <div style="white-space:pre-wrap; font-size:0.9rem; color:#111827; margin-bottom:1.5rem;">
          {{ reader.note_md }}
        </div>
        <details>
          <summary style="font-size:0.85rem; font-weight:600; cursor:pointer; color:#6b7280;">
            Full transcript
          </summary>
          <div style="white-space:pre-wrap; font-size:0.85rem; color:#111827; margin-top:0.5rem;">
            {{ reader.transcript_md }}
          </div>
        </details>
      </div>
    </div>
  </div>
</template>

<script>
import ChatHero from '../components/ChatHero.vue'
import MessageList from '../components/MessageList.vue'
import MessageInput from '../components/MessageInput.vue'
import FocusBadge from '../components/FocusBadge.vue'
import SourcesPanel from '../components/SourcesPanel.vue'
import ContextPicker from '../components/ContextPicker.vue'
import HistoryPanel from '../components/HistoryPanel.vue'
import ConsentPrompt from '../components/ConsentPrompt.vue'
import { canUseTestBackend, getBackend, setBackend } from '../backend'
import { postChat, resetSession, getRoles, getContexts, saveConversation, listConversations, getConversation, getReingestStatus } from '../api'
import {
  isHistoryEnabled, listChats, getChat, renameChat, deleteChat,
  getConsent, setConsent, currentUserKey, contextKeyFor, giveFeedback
} from '../caiApi'

const SESSION_KEY = 'capacio-chatbot-session-id'
const ROLE_KEY = 'capacio-chatbot-role'
const CONTEXT_KEY = 'capacio-chatbot-context'
const HISTORY_OPEN_KEY = 'capacio-chatbot-history-open'
const CONSENT_ASKED_KEY = 'capacio-cai-consent-asked'

function newSessionId () {
  if (window.crypto && window.crypto.randomUUID) {
    return window.crypto.randomUUID().slice(0, 8)
  }
  return Math.random().toString(36).slice(2, 10)
}

export default {
  components: {
    ChatHero,
    MessageList,
    MessageInput,
    FocusBadge,
    SourcesPanel,
    ContextPicker,
    HistoryPanel,
    ConsentPrompt
  },

  data () {
    return {
      sessionId: '',
      contexts: [], // [{ id, name, parent, status, description }] from /contexts
      context: '', // active context pack id (e.g. 'general', 'football')
      contextName: '', // its display name, for the header chip
      org: null, // data/knowledge org for the active context (from GET /contexts' `org` field)
      contextChosen: false, // gates the start picker
      roles: [],
      role: '',
      started: false,
      inflight: false,
      // What the "thinking" indicator shows right now — set to a generic
      // default as soon as a turn starts, then replaced by each tool-call
      // status the API streams back (see api.js's postChat/onStatus).
      currentTask: '',
      messages: [],
      focus: [],
      sources: [],
      panelOpen: false,
      // Remembered across the panel being closed/reopened; null until the
      // panel has picked (or the user has dragged) a width.
      sourcesPanelWidth: null,
      // Saved-conversations feature — off for now, kept wired for a quick re-enable.
      savedFeatureEnabled: false,
      savedList: [],
      savedMenuOpen: false,
      saveVisibility: 'private',
      saving: false,
      saveFlash: false,
      reader: null,
      reingestRunning: false,
      // Saved chat history (CAI database). Everything below stays hidden unless
      // the chatbot reports saving is switched on (GET /health → cai_history).
      historyEnabled: false,
      // Open by default on wide screens, closed on phones (where it covers the chat).
      historyOpen: localStorage.getItem(HISTORY_OPEN_KEY) !== null
        ? localStorage.getItem(HISTORY_OPEN_KEY) === '1'
        : window.innerWidth >= 768,
      chats: [],
      chatsLoading: false,
      chatsError: false,
      consent: null, // true / false once known
      consentAnswered: false, // this user already answered the question in this browser
      consentBusy: false,
      activeConversationId: null, // the saved chat this conversation is stored in
      // A/B switch (internal ChatGPT comparison): A = live, B = test app.
      canSwitch: canUseTestBackend(),
      backend: getBackend()
    }
  },

  computed: {
    // The active environment's name, as the landing page titles it.
    envTitle () {
      if (!this.context || this.context === 'general') return 'CAI'
      // Drop the pack name's parenthetical ("Football (Soccer)") — the picker
      // and the landing page title it the same way.
      return `CAI ${String(this.contextName || this.context).replace(/\s*\([^)]*\)\s*$/, '')}`
    },
    hasSources () {
      return this.sources.some(s => s.type === 'radar' || s.type === 'document')
    },
    // Ask once: saving is on, the user hasn't said yes, and hasn't answered here before.
    showConsentPrompt () {
      return this.historyEnabled && this.consent === false && !this.consentAnswered
    }
  },

  watch: {
    // Role persists across reloads, independent of session/reset.
    role (value) {
      localStorage.setItem(ROLE_KEY, value)
    }
  },

  created () {
    this.sessionId = localStorage.getItem(SESSION_KEY)
    if (!this.sessionId) {
      this.sessionId = newSessionId()
      localStorage.setItem(SESSION_KEY, this.sessionId)
    }
    // The start picker (general vs a sport) opens first; roles load once chosen.
    this.loadContexts()
    this.checkReingest()
    this.initHistory()
  },

  methods: {
    // ── Saved chat history ────────────────────────────────────────────────
    async initHistory () {
      this.historyEnabled = await isHistoryEnabled()
      if (!this.historyEnabled) return
      this.consentAnswered = localStorage.getItem(this.consentAskedKey()) === '1'
      this.loadConsent()
      this.loadChats()
    },

    consentAskedKey () {
      return `${CONSENT_ASKED_KEY}:${currentUserKey()}`
    },

    async loadConsent () {
      try {
        const { data } = await getConsent()
        this.consent = !!data.granted
      } catch (err) {
        this.consent = null // unknown: don't ask, don't show the switch as on
      }
    },

    async loadChats () {
      this.chatsLoading = true
      try {
        const { data } = await listChats()
        this.chats = Array.isArray(data) ? data : []
        this.chatsError = false
      } catch (err) {
        this.chatsError = true
      } finally {
        this.chatsLoading = false
      }
    },

    async answerConsent (yes) {
      if (this.consentBusy) return
      this.consentBusy = true
      try {
        const { data } = await setConsent(!!yes)
        this.consent = !!data.granted
        this.consentAnswered = true
        localStorage.setItem(this.consentAskedKey(), '1')
      } catch (err) {
        window.alert('Your choice could not be saved. Please try again.')
      } finally {
        this.consentBusy = false
      }
    },

    toggleHistory () {
      this.historyOpen = !this.historyOpen
      localStorage.setItem(HISTORY_OPEN_KEY, this.historyOpen ? '1' : '0')
    },

    // On a phone the panel covers the chat, so close it once a chat is picked.
    closeHistoryOnPhone () {
      if (window.innerWidth < 768) this.historyOpen = false
    },

    startNewChat () {
      this.resetConversation()
      this.closeHistoryOnPhone()
    },

    // Open a saved chat: show its messages, switch to its agent, and continue
    // it — the chat's own id becomes the session id, so the chatbot reloads its
    // earlier messages and saves new ones into the same chat.
    async openChat (id) {
      if (this.inflight) return
      try {
        const { data } = await getChat(id)
        const saved = Array.isArray(data.messages) ? data.messages : []
        const match = this.contexts.find(c => contextKeyFor(c.id) === data.contextKey)
        if (match && match.id !== this.context) await this.selectContext(match)
        this.messages = saved.map(m => ({
          role: m.role,
          content: m.content,
          attachmentName: m.attachmentName || null,
          reopened: true,
          id: m.role === 'assistant' ? m.id : null,
          rating: null // earlier ratings aren't sent back by the API yet
        }))
        this.sources = saved.reduce(
          (acc, m) => this.mergeSources(acc, Array.isArray(m.sources) ? m.sources : []), []
        )
        this.focus = []
        this.panelOpen = false
        this.sessionId = data.id
        localStorage.setItem(SESSION_KEY, this.sessionId)
        this.activeConversationId = data.id
        this.started = this.messages.length > 0
        this.closeHistoryOnPhone()
        this.scrollToBottom()
      } catch (err) {
        window.alert('This chat could not be opened.')
        if (err.response && err.response.status === 404) this.loadChats()
      }
    },

    // Thumbs up/down (+ optional comment) on a saved answer.
    async sendFeedback ({ message, rating, comment }) {
      if (!message || !message.id) return
      const before = message.rating
      this.$set(message, 'rating', rating)
      try {
        await giveFeedback(message.id, rating, comment)
      } catch (err) {
        this.$set(message, 'rating', before)
        window.alert('Your feedback could not be saved. Please try again.')
      }
    },

    async renameSavedChat ({ id, title }) {
      try {
        const { data } = await renameChat(id, title)
        this.chats = this.chats.map(c => (c.id === id ? data : c))
      } catch (err) {
        window.alert('The chat could not be renamed.')
      }
    },

    async deleteSavedChat (id) {
      try {
        await deleteChat(id)
        this.chats = this.chats.filter(c => c.id !== id)
        if (id === this.activeConversationId) await this.resetConversation()
      } catch (err) {
        window.alert('The chat could not be deleted.')
      }
    },

    // A/B switch: each chatbot has its own memory, so end the current
    // conversation on the old one, then reload what depends on the chatbot.
    // Saved history only shows when that chatbot saves chats (B does not).
    async switchBackend () {
      if (this.inflight) {
        this.backend = getBackend()
        return
      }
      const chosen = this.backend
      await this.resetConversation()
      setBackend(chosen)
      this.backend = getBackend()
      this.historyEnabled = false
      this.chats = []
      this.consent = null
      await this.loadContexts()
      if (this.contextChosen) await this.loadRoles()
      await this.initHistory()
    },

    async checkReingest () {
      try {
        const { data } = await getReingestStatus()
        this.reingestRunning = !!data.running
      } catch (err) {
        // silently ignore — re-ingest check is best-effort
      }
    },

    async loadContexts () {
      try {
        const { data } = await getContexts()
        this.contexts = data.contexts || []
      } catch (err) {
        console.error('Failed to load contexts', err)
        // Never leave the user stuck at an empty picker — offer general at least.
        this.contexts = [
          { id: 'general', name: 'General Cognition', parent: 'general', status: 'active' }
        ]
      }
    },

    async selectContext (ctx) {
      const changed = this.context !== ctx.id
      this.context = ctx.id
      this.contextName = ctx.name
      this.org = ctx.org || null // switch data/knowledge org with the sport (server-resolved)
      this.contextChosen = true
      localStorage.setItem(CONTEXT_KEY, ctx.id)
      // Roles are per context; (re)load them when the context actually changes.
      // History is untouched, so switching mid-conversation just re-frames it.
      if (changed || !this.roles.length) await this.loadRoles()
    },

    // "Want to know more?" on the picker: enter the general environment and
    // ask that question straight away, skipping the landing page.
    async askFromPicker ({ context, message }) {
      await this.selectContext(context)
      this.sendFromHero(message)
    },

    openPicker () {
      // Re-open to switch context mid-conversation; the session (history) is kept.
      this.contextChosen = false
    },

    async loadRoles () {
      try {
        const { data } = await getRoles(this.context, this.org)
        this.roles = data.roles || []
        const saved = localStorage.getItem(ROLE_KEY)
        // Keep the saved role only if it exists in THIS context; otherwise clear it.
        this.role = (saved && this.roles.some(r => r.id === saved)) ? saved : ''
      } catch (err) {
        console.error('Failed to load roles', err)
        this.roles = []
        this.role = ''
      }
    },

    sendFromHero (message, attachment) {
      this.started = true
      this.send(message, attachment)
    },

    // `attachment` (optional): { name, text, truncated } from MessageInput's
    // POST /chat/upload — extracted server-side, folded into the prompt for
    // this turn only (see ChatRequest.attachment_text on the API side).
    async send (message, attachment) {
      if (this.inflight) return
      this.inflight = true
      this.currentTask = 'Thinking'
      this.messages.push({
        role: 'user',
        content: message,
        attachmentName: attachment ? attachment.name : null
      })
      this.scrollToBottom()
      try {
        const data = await postChat(
          message, this.sessionId, this.role || null, this.context || null, this.org || null,
          label => { this.currentTask = label },
          attachment ? attachment.text : null,
          attachment ? attachment.name : null
        )
        this.messages.push({
          role: 'assistant',
          content: data.reply || '_(empty response)_',
          id: data.message_id || null, // saved answer's id: enables thumbs up/down
          rating: null
        })
        // Saved (user said yes to saving): remember which saved chat this is and
        // refresh the list so it appears / moves to the top.
        if (data.conversation_id && this.historyEnabled) {
          this.activeConversationId = data.conversation_id
          this.loadChats()
        }
        // Only refresh when this turn referenced a player; otherwise keep the
        // previous badge (the conversation is still "about" that player).
        if (data.focus && data.focus.length) {
          this.focus = data.focus
        }
        // Merge rather than replace: a follow-up turn about the same player
        // (e.g. asking for more detail) often comes back with no `sources`
        // of its own, and replacing wholesale would blank out — and hide —
        // context that's still relevant to the conversation.
        this.sources = this.mergeSources(this.sources, data.sources || [])
        // Don't auto-open the panel on every reply — it stays closed (with
        // the edge tab available) until the user opens it themselves, e.g.
        // via the edge tab or a citation click (focusSourcesPanel).
      } catch (err) {
        const detail = (err.response && err.response.data && err.response.data.detail)
          ? err.response.data.detail
          : err.response
            ? `HTTP ${err.response.status}: ${JSON.stringify(err.response.data)}`
            : `Network error: ${err.message}`
        this.messages.push({ role: 'assistant', content: `⚠️ ${detail}` })
      } finally {
        this.inflight = false
        this.currentTask = ''
        this.scrollToBottom()
      }
    },

    // Identifies "the same source" across turns so a repeat (or refreshed)
    // mention merges in place instead of duplicating.
    sourceKey (s) {
      if (s.type === 'radar') return `radar:${s.player}`
      if (s.type === 'document') return `document:${s.file}`
      return `${s.type}:${JSON.stringify(s)}`
    },

    mergeSources (existing, incoming) {
      const merged = existing.slice()
      incoming.forEach(s => {
        const key = this.sourceKey(s)
        const idx = merged.findIndex(m => this.sourceKey(m) === key)
        if (idx === -1) merged.push(s)
        else merged.splice(idx, 1, s)
      })
      return merged
    },

    scrollToBottom () {
      this.$nextTick(() => {
        const col = this.$refs.chatCol
        if (col) col.scrollTo({ top: col.scrollHeight, behavior: 'smooth' })
      })
    },

    focusSourcesPanel () {
      if (this.hasSources) this.panelOpen = true
    },

    closeSources () {
      this.panelOpen = false
    },

    reopenSources () {
      this.panelOpen = true
    },

    async toggleSavedMenu () {
      if (this.savedMenuOpen) {
        this.savedMenuOpen = false
        return
      }
      this.savedMenuOpen = true
      await this.loadSaved()
    },

    async loadSaved () {
      try {
        const { data } = await listConversations(this.org)
        this.savedList = data.conversations || []
      } catch (err) {
        console.error('Failed to load saved conversations', err)
        this.savedList = []
      }
    },

    async openSaved (id) {
      this.savedMenuOpen = false
      try {
        const { data } = await getConversation(id)
        this.reader = data
      } catch (err) {
        console.error('Failed to open saved conversation', err)
        this.reader = null
      }
    },

    closeReader () {
      this.reader = null
    },

    async saveConversationToKnowledge () {
      if (this.saving) return
      this.saving = true
      try {
        const { data } = await saveConversation(this.sessionId, {
          visibility: this.saveVisibility,
          context: this.context || null,
          org: this.org || null
        })
        this.saveFlash = true
        setTimeout(() => { this.saveFlash = false }, 2000)
        if (this.savedMenuOpen) await this.loadSaved()
        return data
      } catch (err) {
        const detail = err.response
          ? (err.response.data && err.response.data.detail) || `HTTP ${err.response.status}`
          : `Network error: ${err.message}`
        window.alert(`Not saved. ${detail}`)
      } finally {
        this.saving = false
      }
    },

    async resetConversation () {
      try {
        await resetSession(this.sessionId)
      } catch (err) { /* best effort — a stale session is dropped server-side anyway */ }
      this.sessionId = newSessionId()
      localStorage.setItem(SESSION_KEY, this.sessionId)
      this.messages = []
      this.focus = []
      this.sources = []
      this.panelOpen = false
      this.activeConversationId = null
      // Back to the hero, still in the same context — a fresh session
      // starts instantly instead of re-asking which context to use.
      this.started = false
    }
  }
}
</script>

<style lang="scss">
@import "../chatbot.scss";
@import "../cai-history.scss";
</style>
